/**
 * The title theme (05 §3): an original tune, C major, 144 BPM, 36 bars that loop at exactly
 * 60 seconds. Verse - chorus - bridge - chorus - turnaround.
 *
 * The hook is the verse's first bar, "C D E G~ E D": a climb that lands on a syncopated G and
 * steps back, answered a bar later by a fall to A. The chorus is a dotted 3 + 3 + 2 figure
 * ("A A G A C") repeated down the chords like a sequence, so it sticks after one hearing. The
 * bridge slows to long notes over held chords (the vibrato shows here), and the turnaround's
 * F to F minor is the one dark colour before the loop comes home.
 */

import { BOUNCE, DRIVE, EIGHTHS, HALF, TRESILLO } from './grooves';
import type { Song } from './types';

export const TITLE: Song = {
  bpm: 144,
  loop: true,
  bassFloor: 36,
  arpFloor: 55,
  mix: { lead: 0.2, arp: 0.09, bass: 0.26, drum: 0.3 },
  order: ['verse', 'chorus', 'bridge', 'chorus', 'turn'],
  patterns: {
    verse: {
      chords: 'C | Am | F | G | C | Am | Dm G | C',
      lead: `
        C5 . D5 . E5 . G5 . . . E5 . D5 . . . |
        E5 . C5 . A4 . . . . . - . G4 . A4 . |
        C5 . C5 . F5 . A5 . . . G5 . F5 . . . |
        E5 . D5 . B4 . . . . . - . G4 . B4 . |
        C5 . D5 . E5 . G5 . . . E5 . G5 . A5 . |
        C6 . . . B5 . A5 . E5 . . . . . - . |
        F5 . E5 . D5 . F5 . E5 . D5 . B4 . G4 . |
        C5 . . . . . - . . . . . E5 . G5 .`,
      bass: { groove: BOUNCE },
      arp: TRESILLO,
    },
    chorus: {
      chords: 'F | G | Em | Am | Dm | G | C | C',
      lead: `
        A5 . . A5 . . G5 . A5 . . . C6 . . . |
        B5 . . B5 . . A5 . G5 . . . D5 . . . |
        G5 . . G5 . . E5 . G5 . . . B5 . . . |
        A5 . . . . . . . - . . . E5 . G5 . |
        A5 . . A5 . . G5 . F5 . . . A5 . . . |
        G5 . . G5 . . F5 . D5 . . . B4 . . . |
        C5 . E5 . G5 . C6 . . . . . . . - . |
        - . . . . . . . G5 . E5 . C5 . - .`,
      bass: { groove: DRIVE },
      arp: EIGHTHS,
    },
    bridge: {
      chords: 'Am | Em | F | C | Dm | Em | F | G',
      lead: `
        E5 . . . A5 . . . G5 . . . E5 . . . |
        D5 . . . E5 . . . B4 . . . . . . . |
        C5 . . . F5 . . . E5 . . . C5 . . . |
        D5 . . . E5 . . . G5 . . . . . . . |
        F5 . . . A5 . . . G5 . . . F5 . . . |
        E5 . . . G5 . . . B5 . . . . . . . |
        A5 . . . C6 . . . B5 . A5 . G5 . F5 . |
        G5 . . . - . D5 . G5 . B5 . D6 . . .`,
      bass: { groove: HALF },
    },
    turn: {
      chords: 'F | Fm | C | G7',
      lead: `
        C6 . . . . . A5 . F5 . . . . . . . |
        C6 . . . . . Ab5 . F5 . . . . . . . |
        E5 . G5 . C6 . . . G5 . E5 . C5 . . . |
        G5 . . . F5 . D5 . B4 . . . G4 . . .`,
      bass: { groove: BOUNCE },
      arp: TRESILLO,
    },
  },
};
